import Konva from "konva";
import React from "react";
import { Circle, Group, Line, Rect, Text } from "react-konva";
import { ZoneShape as IZoneShape } from "../../../../types";
import { getWallDotPosition } from "../../../../utils/bezier";
import { stripHexAlpha } from "../../../../utils/color";
import { mergeRefs } from "../../../../utils/mergeRef";
import {
  normalizeZonePolygon,
  sampleZonePolygonPoints
} from "../../../../utils/zonePolygon";
import {
  useMapCanvas,
  useMapCanvasStage
} from "../../contexts/MapCanvasContext";
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_ZONE_BORDER_COLOR,
  DEFAULT_ZONE_BORDER_WIDTH,
  DEFAULT_ZONE_FILL_OPACITY
} from "../../defines";
import { ShapeCommonProps } from "../../types";
import { useShape } from "../ShapeContext/useShape";

export const ZoneShape = React.forwardRef<
  React.ComponentRef<typeof Group>,
  ShapeCommonProps
>((commonProps, ref) => {
  const { shape, isSelected, onUpdate } = useShape();
  const zoneShape = shape as IZoneShape;
  const { zoneTypes, isViewMode } = useMapCanvas();
  // Changes only on zoom: labels that would render smaller than a few
  // pixels are skipped, which is most of them on a zoomed-out floor.
  const { stageScale } = useMapCanvasStage();

  // When the zone is assigned to a category, paint with the category's
  // color (fill + border) so the editor matches what the legend shows
  // in the viewer. Per-zone color/borderColor act as fallbacks.
  const category = zoneShape.categoryTypeId
    ? zoneTypes.find((t) => t.id === zoneShape.categoryTypeId)
    : undefined;
  // Opacity is controlled exclusively by fillOpacity — strip any alpha
  // baked into the color (e.g. the old '#3B82F620' default) so the two
  // don't multiply into a nearly-invisible fill.
  const rawFillColor = category?.color || zoneShape.color;
  const fillColor = rawFillColor ? stripHexAlpha(rawFillColor) : rawFillColor;
  const borderColor =
    category?.color || zoneShape.borderColor || DEFAULT_ZONE_BORDER_COLOR;

  const groupRef = React.useRef<React.ComponentRef<typeof Group>>(null);

  // ---- Polygon geometry (relative to the zone origin) ----
  const isPolygon = (zoneShape.vertices?.length ?? 0) >= 3;

  // Draft state so handle drags redraw the outline live and only commit
  // (with bbox re-anchoring) on drag end — same pattern as wall-paths.
  const [draftVertices, setDraftVertices] = React.useState<
    { x: number; y: number }[] | null
  >(null);
  const [draftEdge, setDraftEdge] = React.useState<{
    index: number;
    curvature: number;
    curvatureCenter: number;
  } | null>(null);

  const effectiveVertices = draftVertices || zoneShape.vertices || [];
  const effectiveEdges = React.useMemo(() => {
    const base = [...(zoneShape.edges || [])];
    while (base.length < effectiveVertices.length) base.push({});
    if (draftEdge) {
      base[draftEdge.index] = {
        curvature: draftEdge.curvature,
        curvatureCenter: draftEdge.curvatureCenter
      };
    }
    return base;
  }, [zoneShape.edges, effectiveVertices.length, draftEdge]);

  const polygonPoints = React.useMemo(() => {
    if (!isPolygon) return [];
    return sampleZonePolygonPoints(effectiveVertices, effectiveEdges).flatMap(
      (point) => [point.x, point.y]
    );
  }, [isPolygon, effectiveVertices, effectiveEdges]);

  const commitPolygon = (
    vertices: { x: number; y: number }[],
    edges: IZoneShape["edges"]
  ) => {
    onUpdate(normalizeZonePolygon(zoneShape, vertices, edges));
  };

  const handleVertexDragMove =
    (index: number) => (e: Konva.KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      const pos = e.target.position();
      setDraftVertices((prev) => {
        const base = prev || zoneShape.vertices || [];
        const next = [...base];
        next[index] = { x: pos.x, y: pos.y };
        return next;
      });
    };

  const handleVertexDragEnd =
    (index: number) => (e: Konva.KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      const pos = e.target.position();
      const base = draftVertices || zoneShape.vertices || [];
      const next = [...base];
      next[index] = { x: Math.round(pos.x), y: Math.round(pos.y) };
      setDraftVertices(null);
      commitPolygon(next, effectiveEdges);
    };

  const handleCurvatureDragMove =
    (
      index: number,
      from: { x: number; y: number },
      to: { x: number; y: number }
    ) =>
    (e: Konva.KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      const pos = e.target.position();
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return;
      const ux = dx / len;
      const uy = dy / len;
      const px = -uy;
      const py = ux;
      const lx = pos.x - from.x;
      const ly = pos.y - from.y;
      const along = lx * ux + ly * uy;
      const perp = lx * px + ly * py;
      const center = Math.max(0.05, Math.min(0.95, along / len));
      // Keep the handle pinned to the curve peak it controls.
      e.target.position({
        x: from.x + center * len * ux + perp * px,
        y: from.y + center * len * uy + perp * py
      });
      setDraftEdge({ index, curvature: perp, curvatureCenter: center });
    };

  const handleCurvatureDragEnd =
    (index: number) => (e: Konva.KonvaEventObject<DragEvent>) => {
      e.cancelBubble = true;
      if (!draftEdge) return;
      const newEdges = [...effectiveEdges];
      newEdges[index] = {
        curvature: Math.round(draftEdge.curvature),
        curvatureCenter: Math.round(draftEdge.curvatureCenter * 100) / 100
      };
      setDraftEdge(null);
      commitPolygon(effectiveVertices, newEdges);
    };

  const showHandles = isPolygon && isSelected && !isViewMode;

  // Name placement within the zone bounds (default top-left). Same 8px
  // padding + text-top math as the SVG viewer so both render identically.
  const labelPosition = zoneShape.labelPosition || "top-left";
  const labelFontSize = zoneShape.labelFontSize || 14;
  const isLabelCentered = labelPosition.endsWith("center");
  const labelY =
    labelPosition === "center"
      ? zoneShape.height / 2 - labelFontSize / 2
      : labelPosition.startsWith("bottom")
        ? zoneShape.height - 8 - labelFontSize
        : 8;
  const isLabelLegible =
    labelFontSize * stageScale >= 5 && zoneShape.width * stageScale >= 24;

  // Render fill and border separately so opacity can apply to the fill
  // alone — Konva's `opacity` attribute affects the entire node (both
  // fill and stroke), and we want the border to read at full strength
  // while the interior stays translucent. The fill doesn't listen so
  // shapes inside the zone stay selectable; the dashed border is the
  // click target, like the rectangle version.
  return (
    <Group
      {...commonProps}
      // Explicit width/height so Konva.Transformer's resize bake (which
      // computes `node.width() * scaleX`) has a non-zero starting point.
      // Without these, resizing a zone collapses it to the 5px minimum.
      width={zoneShape.width}
      height={zoneShape.height}
      ref={mergeRefs([groupRef, ref])}
    >
      {isPolygon ? (
        <>
          <Line
            points={polygonPoints}
            closed
            fill={fillColor}
            opacity={zoneShape.fillOpacity ?? DEFAULT_ZONE_FILL_OPACITY}
            listening={false}
            perfectDrawEnabled={false}
          />
          <Line
            points={polygonPoints}
            closed
            stroke={borderColor}
            strokeWidth={zoneShape.borderWidth || DEFAULT_ZONE_BORDER_WIDTH}
            dash={[8, 4]}
            hitStrokeWidth={12}
            perfectDrawEnabled={false}
          />
        </>
      ) : (
        <>
          <Rect
            width={zoneShape.width}
            height={zoneShape.height}
            fill={fillColor}
            opacity={zoneShape.fillOpacity ?? DEFAULT_ZONE_FILL_OPACITY}
            cornerRadius={zoneShape.cornerRadius || 0}
            listening={false}
            perfectDrawEnabled={false}
          />
          <Rect
            width={zoneShape.width}
            height={zoneShape.height}
            stroke={borderColor}
            strokeWidth={zoneShape.borderWidth || DEFAULT_ZONE_BORDER_WIDTH}
            dash={[8, 4]}
            cornerRadius={zoneShape.cornerRadius || 0}
            perfectDrawEnabled={false}
          />
        </>
      )}
      {isLabelLegible ? (
        <Text
          text={zoneShape.name}
          x={8}
          y={labelY}
          // Wrap inside the zone with 8px side padding — the SVG viewer
          // wraps at the same width so the name never bleeds out there.
          width={Math.max(zoneShape.width - 16, labelFontSize)}
          align={isLabelCentered ? "center" : "left"}
          fontSize={labelFontSize}
          fill={borderColor}
          fontFamily={DEFAULT_FONT_FAMILY}
          listening={false}
          perfectDrawEnabled={false}
        />
      ) : null}
      {showHandles &&
        effectiveVertices.map((vertex, index) => (
          <Circle
            // biome-ignore lint/suspicious/noArrayIndexKey: vertices have no ids; index is their identity
            key={`zone-vertex-${index}`}
            x={vertex.x}
            y={vertex.y}
            radius={5}
            fill="#ffffff"
            stroke="#2563eb"
            strokeWidth={1.5}
            draggable
            onDragStart={(e) => {
              e.cancelBubble = true;
            }}
            onDragMove={handleVertexDragMove(index)}
            onDragEnd={handleVertexDragEnd(index)}
            onMouseDown={(e) => {
              e.cancelBubble = true;
            }}
          />
        ))}
      {showHandles &&
        effectiveVertices.map((vertex, index) => {
          const to = effectiveVertices[(index + 1) % effectiveVertices.length];
          const edge = effectiveEdges[index] || {};
          const dot = getWallDotPosition(
            vertex.x,
            vertex.y,
            to.x,
            to.y,
            edge.curvature || 0,
            edge.curvatureCenter ?? 0.5
          );
          return (
            <Circle
              // biome-ignore lint/suspicious/noArrayIndexKey: edges have no ids; index is their identity
              key={`zone-curve-${index}`}
              x={dot.x}
              y={dot.y}
              radius={4}
              fill="#2563eb"
              opacity={0.7}
              draggable
              onDragStart={(e) => {
                e.cancelBubble = true;
              }}
              onDragMove={handleCurvatureDragMove(index, vertex, to)}
              onDragEnd={handleCurvatureDragEnd(index)}
              onMouseDown={(e) => {
                e.cancelBubble = true;
              }}
            />
          );
        })}
    </Group>
  );
});
